import { useCallback, useEffect, useState } from 'react'
import { lumi } from '@/lib/lumi'
import { sanitizeBlockHtml } from '@/lib/sanitizeBlockHtml'
import {Ban, Loader2, ShieldOff, Unlock} from 'lucide-react'
import toast from 'react-hot-toast'
import type { UserProfile } from '@/types'

const DEFAULT_MESSAGE = `<div style="font-family: Georgia, serif; text-align: center; color: #1a1a1a;">
  <h2 style="margin: 0 0 12px; font-size: 28px;">Acesso suspenso</h2>
  <p style="margin: 0; font-size: 16px; line-height: 1.6; color: #444;">O seu acesso ao painel foi bloqueado pelo SISTEMA. Contacte a redacção se precisar de esclarecimentos.</p>
</div>`

const PERMISSION_LABELS: Record<string, string> = {
  admin: 'Administrador',
  editor: 'Editor',
  author: 'Autor',
}

export default function BlockUsers() {
  const [users, setUsers] = useState<UserProfile[]>([])
  const [loading, setLoading] = useState(true)
  const [enabled, setEnabled] = useState(false)
  const [selectedId, setSelectedId] = useState('')
  const [message, setMessage] = useState(DEFAULT_MESSAGE)
  const [saving, setSaving] = useState(false)
  const [unblockingId, setUnblockingId] = useState<string | null>(null)

  const fetchUsers = useCallback(async () => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.user_profiles.list({
        sort: { createdAt: -1 },
        limit: 100,
      })
      setUsers((list || []) as UserProfile[])
    } catch {
      toast.error('Erro ao carregar utilizadores.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  const blockableUsers = users.filter(
    (u) => u.permission !== 'admin' && (u.username || '').toLowerCase() !== 'admin'
  )
  const blockedUsers = blockableUsers.filter((u) => u.isBlocked)
  const selectedUser = blockableUsers.find((u) => u._id === selectedId)

  const handleBlock = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!enabled) {
      toast.error('Marque a caixa para activar o bloqueio.')
      return
    }
    if (!selectedId) {
      toast.error('Seleccione o utilizador que pretende bloquear.')
      return
    }
    if (!message.trim()) {
      toast.error('Cole a mensagem HTML de bloqueio.')
      return
    }

    setSaving(true)
    try {
      const result = await lumi.functions.invoke('createAdminUser', {
        method: 'POST',
        body: {
          action: 'block_user',
          profileId: selectedId,
          blockMessage: message,
        },
      }) as { error?: string }

      if (result?.error) throw new Error(result.error)
      toast.success(`${selectedUser?.displayName || 'Utilizador'} foi bloqueado.`)
      setEnabled(false)
      setSelectedId('')
      fetchUsers()
    } catch (err: unknown) {
      const error = err as { data?: { error?: string }; message?: string }
      toast.error(error?.data?.error || error?.message || 'Erro ao bloquear utilizador.')
    } finally {
      setSaving(false)
    }
  }

  const handleUnblock = async (profile: UserProfile) => {
    setUnblockingId(profile._id)
    try {
      const result = await lumi.functions.invoke('createAdminUser', {
        method: 'POST',
        body: {
          action: 'unblock_user',
          profileId: profile._id,
        },
      }) as { error?: string }

      if (result?.error) throw new Error(result.error)
      toast.success(`${profile.displayName} foi desbloqueado.`)
      fetchUsers()
    } catch (err: unknown) {
      const error = err as { data?: { error?: string }; message?: string }
      toast.error(error?.data?.error || error?.message || 'Erro ao desbloquear.')
    } finally {
      setUnblockingId(null)
    }
  }

  return (
    <div className="mx-auto max-w-4xl p-6">
      <div className="mb-8">
        <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-red-700">
          CARLOS FELIPE
        </p>
        <h1 className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">
          MongoDB AUTH
        </h1>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-gray-500 dark:text-gray-400">
          Área exclusiva de administrador. Marque a caixa, escolha o utilizador e cole o HTML/CSS da mensagem. Ao entrar no painel, ele vê essa mensagem, clica em Compreendo e fica sem acesso até ser desbloqueado.
        </p>
      </div>

      <form
        onSubmit={handleBlock}
        className="overflow-hidden rounded-2xl border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-950"
      >
        <label className="flex cursor-pointer items-start gap-3 border-b border-zinc-100 px-5 py-4 dark:border-zinc-800">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="mt-1 h-4 w-4 rounded border-zinc-300 text-red-600 focus:ring-red-500"
          />
          <span>
            <span className="block text-sm font-semibold text-gray-900 dark:text-white">
              Bloquear utilizador
            </span>
            <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">
              Só depois de marcar esta caixa é possível seleccionar alguém e aplicar o bloqueio.
            </span>
          </span>
        </label>

        <div className={`space-y-5 p-5 ${enabled ? '' : 'pointer-events-none opacity-45'}`}>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Utilizador
            </label>
            {loading ? (
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <Loader2 size={16} className="animate-spin" />
                A carregar...
              </div>
            ) : (
              <select
                value={selectedId}
                onChange={(e) => setSelectedId(e.target.value)}
                disabled={!enabled}
                className="w-full rounded-lg border border-gray-200 bg-white px-3.5 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-red-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
              >
                <option value="">Seleccione o utilizador</option>
                {blockableUsers.map((u) => (
                  <option key={u._id} value={u._id}>
                    {u.displayName} (@{u.username || '—'}) — {PERMISSION_LABELS[u.permission] || u.permission}
                    {u.isBlocked ? ' — já bloqueado' : ''}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Mensagem HTML / CSS
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={!enabled}
              rows={10}
              spellCheck={false}
              className="w-full rounded-lg border border-gray-200 bg-zinc-50 px-3.5 py-3 font-mono text-xs leading-relaxed text-gray-900 focus:outline-none focus:ring-2 focus:ring-red-500 dark:border-gray-700 dark:bg-zinc-900 dark:text-gray-100"
              placeholder="<div style=&quot;color:#111&quot;>Mensagem de bloqueio</div>"
            />
            <p className="mt-1.5 text-xs text-gray-400">
              Cole o código da mensagem. Scripts e eventos JavaScript são removidos por segurança.
            </p>
          </div>

          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
              Pré-visualização
            </p>
            <div className="rounded-xl border border-dashed border-zinc-200 bg-white p-5 dark:border-zinc-800 dark:bg-zinc-900">
              <div dangerouslySetInnerHTML={{ __html: sanitizeBlockHtml(message) }} />
            </div>
          </div>

          <button
            type="submit"
            disabled={!enabled || saving}
            className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-red-700 disabled:opacity-50"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Ban size={16} />}
            Aplicar bloqueio
          </button>
        </div>
      </form>

      <section className="mt-8">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
          Utilizadores bloqueados
        </h2>
        <div className="overflow-hidden rounded-2xl border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-950">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 size={22} className="animate-spin text-red-600" />
            </div>
          ) : blockedUsers.length === 0 ? (
            <div className="px-5 py-12 text-center">
              <ShieldOff size={28} className="mx-auto mb-3 text-gray-300 dark:text-gray-600" />
              <p className="text-sm text-gray-400">Nenhum utilizador bloqueado neste momento.</p>
            </div>
          ) : (
            <div className="divide-y divide-zinc-100 dark:divide-zinc-800">
              {blockedUsers.map((u) => (
                <div key={u._id} className="flex items-center gap-4 px-5 py-4">
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-red-100 dark:bg-red-950">
                    <Ban size={16} className="text-red-700 dark:text-red-400" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-gray-900 dark:text-white">
                      {u.displayName}
                    </p>
                    <p className="truncate text-xs text-gray-400">
                      @{u.username || '—'} · {PERMISSION_LABELS[u.permission] || u.permission}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleUnblock(u)}
                    disabled={unblockingId === u._id}
                    className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-emerald-700 disabled:opacity-50"
                  >
                    {unblockingId === u._id ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Unlock size={14} />
                    )}
                    Desbloquear
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  )
}
